import { http } from "./client";
import type { PageParams } from "@/types/api";
import type {
  AnnualStatement,
  ComplianceSummary,
  DeclarationStatus,
  EmployeeStatutoryInfo,
  FilingStatus,
  FilingType,
  StatutoryFiling,
  StatutorySettings,
  TaxComputation,
  TaxDeclaration,
  TaxRegime,
  Form16,
  ReturnFile,
} from "@/types/domain";

export interface FilingFilters extends PageParams {
  filing_type?: FilingType | "";
  status?: FilingStatus | "";
  year?: number;
}

export interface DeclarationFilters extends PageParams {
  employee_id?: string;
  financial_year?: string;
  status?: DeclarationStatus | "";
}

export const complianceApi = {
  // ---- statutory settings ----
  settings: () => http.get<StatutorySettings>("/compliance/settings"),
  updateSettings: (values: Record<string, unknown>) =>
    http.put<StatutorySettings>("/compliance/settings", values),

  // ---- employee statutory info ----
  employeeInfo: (employeeId: string) => http.get<EmployeeStatutoryInfo>(`/compliance/employees/${employeeId}`),
  updateEmployeeInfo: (employeeId: string, values: Record<string, unknown>) =>
    http.put<EmployeeStatutoryInfo>(`/compliance/employees/${employeeId}`, values),

  // ---- filings ----
  summary: () => http.get<ComplianceSummary>("/compliance/summary"),
  filings: (filters: FilingFilters = {}) => http.getPage<StatutoryFiling>("/compliance/filings", filters),
  filing: (id: string) => http.get<StatutoryFiling>(`/compliance/filings/${id}`),
  generateFiling: (filingType: FilingType, period: string) =>
    http.post<StatutoryFiling>("/compliance/filings/generate", { filing_type: filingType, period }),
  markFiled: (id: string, referenceNumber: string) =>
    http.post<StatutoryFiling>(`/compliance/filings/${id}/file`, { reference_number: referenceNumber }),
  recordPayment: (id: string, paymentReference: string) =>
    http.post<StatutoryFiling>(`/compliance/filings/${id}/record-payment`, { payment_reference: paymentReference }),

  // ---- tax declarations ----
  declarations: (filters: DeclarationFilters = {}) => http.getPage<TaxDeclaration>("/compliance/declarations", filters),
  upsertDeclaration: (values: Record<string, unknown>) => http.post<TaxDeclaration>("/compliance/declarations", values),
  verifyDeclaration: (id: string, approve: boolean, notes?: string) =>
    http.post<TaxDeclaration>(`/compliance/declarations/${id}/verify`, { approve, notes }),

  // ---- employee self-service ----
  myStatutoryInfo: () => http.get<EmployeeStatutoryInfo>("/compliance/my-statutory-info"),

  // Income Tax & Statutory Returns (feature income_tax_returns).
  taxComputation: (employeeId: string, financialYear: string) =>
    http.get<TaxComputation>("/compliance/tax-computation", { employee_id: employeeId, financial_year: financialYear }),
  form16: (employeeId: string, financialYear: string) =>
    http.get<Form16>("/compliance/form16", { employee_id: employeeId, financial_year: financialYear }),
  pfECR: (period: string) => http.get<ReturnFile>("/compliance/returns/pf-ecr", { period }),
  esiReturn: (period: string) => http.get<ReturnFile>("/compliance/returns/esi", { period }),
  return24Q: (financialYear: string, quarter: number) =>
    http.get<ReturnFile>("/compliance/returns/24q", { financial_year: financialYear, quarter }),
  myTaxComputation: (financialYear: string) =>
    http.get<TaxComputation>("/compliance/my-tax-computation", { financial_year: financialYear }),
  myForm16: (financialYear: string) => http.get<Form16>("/compliance/my-form16", { financial_year: financialYear }),
  setMyTaxRegime: (financialYear: string, taxRegime: TaxRegime) =>
    http.put<TaxDeclaration>("/compliance/my-tax-regime", { financial_year: financialYear, tax_regime: taxRegime }),
  myAnnualStatement: (financialYear?: string) =>
    http.get<AnnualStatement>("/compliance/my-annual-statement", financialYear ? { financial_year: financialYear } : undefined),
  myTaxDeclaration: (financialYear?: string) =>
    http.get<TaxDeclaration>("/compliance/my-tax-declaration", financialYear ? { financial_year: financialYear } : undefined),
};
